/**
 * Minimal shape needed to collapse a document list to its latest versions.
 * Kept structural so the helper works on both MST `DocumentModel` instances
 * and the plain cache objects held in `projectDocuments`.
 */
interface VersionedRow {
  id: string | number;
  version: number | null;
  version_group: string | null;
}

/** Is `a` a newer version than `b` within the same version_group? */
function isNewerVersion(a: VersionedRow, b: VersionedRow): boolean {
  const av = a.version ?? 0;
  const bv = b.version ?? 0;
  if (av !== bv) return av > bv;
  // Equal version numbers shouldn't occur inside one lineage, but fall back to
  // a deterministic id tie-breaker so the result never depends on array order.
  return Number(a.id) > Number(b.id);
}

/**
 * Collapse a list of document rows to one row per version lineage: the latest
 * version (highest `version`) of each `version_group`.
 *
 * A new document version is stored as a NEW row (new id, incremented `version`,
 * same `version_group`) while the previous row's `is_latest_version` is flipped
 * to false. The store learns of the new row and the flip through separate
 * realtime events, so for a brief window — or if the flip event is missed or
 * arrives out of order — both versions can coexist in the store. Rendering both
 * makes the list show an older version's row (and its older tags) for what the
 * user believes is the current document. This pure read-time guard guarantees
 * each lineage only ever surfaces its latest version, independent of realtime
 * event timing.
 *
 * Rows with a null `version_group` are standalone (single-version / legacy) and
 * are always kept — each is its own lineage. Input order is preserved, and the
 * original array reference is returned untouched when nothing is versioned (the
 * common case), keeping referential stability for memoised consumers.
 */
export function keepLatestVersions<T extends VersionedRow>(docs: T[]): T[] {
  const latestByGroup = new Map<string, T>();
  for (const doc of docs) {
    if (doc.version_group == null) continue;
    const existing = latestByGroup.get(doc.version_group);
    if (!existing || isNewerVersion(doc, existing)) {
      latestByGroup.set(doc.version_group, doc);
    }
  }

  // Fast path: no grouped lineages → nothing to collapse, keep the same array.
  if (latestByGroup.size === 0) return docs;

  const chosen = new Set<T>(latestByGroup.values());
  return docs.filter((doc) => doc.version_group == null || chosen.has(doc));
}
